/* eslint-disable react/prop-types */
import React from "react";
import { Typography, Box, makeStyles } from "@material-ui/core";
import moment from "moment";
import _ from "lodash";
import { useSelector } from "react-redux";
import { SellOut, PriceOut } from "resources/constants";
import { applyCorrectionToEstimation } from "lib/utils/estimations";
import TimeSeriesChartComponent from "./TimeSeriesChartComponent";

const useStyles = makeStyles((theme) => ({
  flexCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  alignBaseline: {
    alignItems: "baseline",
  },
  sectionContainer: {
    display: "flex",
    flexDirection: "column",
    height: "50%",
  },
  sectionTitle: {
    fontWeight: "bold",
    fontSize: "14px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "16px",
    },
  },
  chartcontainer: {
    flexGrow: 1,
    overflow: "hidden",
    position: "relative",
  },
}));

function PriceTimeSeriesComponent({
  data,
  numHistoricMonths = 6,
  numEstimationMonths = 5,
}) {
  const classes = useStyles();
  const configCatSlice = useSelector((state) => state.auth.configCatUser);
  const sellOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? SellOut
    : "Sell Out";
  const priceOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? PriceOut
    : "Price Out";

  const getXLabels = () => {
    const getMonthLabels = (from, to) => {
      const fromDate = moment(from);
      const toDate = moment(to);
      const monthLabels = [];

      while (fromDate <= toDate) {
        monthLabels.push(fromDate.format("MMM'YY"));
        fromDate.add(1, "month");
      }

      return monthLabels;
    };
    return getMonthLabels(
      moment(data.own?.lastHistoricDate).subtract(numHistoricMonths, "months"),
      moment(data.own?.lastHistoricDate).add(numEstimationMonths, "months")
    );
  };

  const labels = getXLabels();

  const correctAndReduceEstimations = (targetData, sourceData) => {
    const correctedEstimations = _.map(
      targetData.estimations,
      (targetEstimation) => {
        const sourceEstimation =
          sourceData.estimations?.[
            `${targetEstimation.date}-${sourceData.selectedProduct?.key}-${targetEstimation.area_id}-${targetEstimation.retailer_id}`
          ];

        if (sourceEstimation) {
          const crossAdjustment =
            data.crossAdjustments?.[
              `${targetEstimation.date}-${sourceData.selectedProduct?.key}-${targetEstimation.product_id}-${targetEstimation.area_id}-${targetEstimation.retailer_id}`
            ];

          if (!crossAdjustment) return targetEstimation;

          return applyCorrectionToEstimation(
            sourceEstimation,
            targetEstimation,
            crossAdjustment
          );
        }

        return targetEstimation;
      }
    );

    return _(correctedEstimations)
      .groupBy("date")
      .mapValues((a) => ({
        sum_units: _.sumBy(a, "units"),
      }))
      .value();
  };

  const getDatasets = (targetData, sourceData, color) => {
    const minDate = moment(data.own?.lastHistoricDate).subtract(
      numHistoricMonths,
      "months"
    );
    const maxDate = moment(data.own?.lastHistoricDate).add(
      numEstimationMonths,
      "months"
    );

    const filteredHistorics = _.filter(
      targetData[targetData?.selectedProduct?.key]?.historics,
      (historic) => {
        const date = moment(historic.date);
        return date >= minDate && date <= maxDate;
      }
    );

    const historicsDataset = {
      backgroundColor: color,
      borderColor: color,
      fill: false,
      hoverBorderColor: color,
      hoverBorderWidth: 3,
      label: `${color}-historics`,
      pointBackgroundColor: "#ffffff",
      pointBordercolor: "rgba(165, 21, 87,1)",
      pointHoverBackgroundColor: "#ffffff",
      pointHoverRadius: 6,
      title: targetData?.selectedProduct?.label,
      type: "line",
    };

    const historicsPriceDataset = {
      ...historicsDataset,
      yAxisID: "price",
      data: [],
    };

    const historicsUnitsDataset = {
      ...historicsDataset,
      yAxisID: "units",
      data: [],
    };

    // Iterate once through the array to populate the price and sellout datasets 'data'
    _.forEach(filteredHistorics, (historic) => {
      const shortDate = moment(historic.date).format("MMM'YY");

      historicsPriceDataset.data.push({
        date: historic.date,
        x: shortDate,
        y: historic.avg_weight_by_units_price,
      });

      historicsUnitsDataset.data.push({
        date: historic.date,
        x: shortDate,
        y: historic.sum_units,
      });
    });

    const estimationsDataset = {
      backgroundColor: color,
      borderColor: color,
      borderDash: [4, 3],
      fill: false,
      hoverBorderColor: color,
      hoverBorderWidth: 3,
      label: `${color}-estimations`,
      pointBackgroundColor: "#ffffff",
      pointBordercolor: "rgba(165, 21, 87, 1)",
      pointHoverBackgroundColor: "#ffffff",
      pointHoverRadius: 6,
      title: targetData?.selectedProduct?.label,
      type: "line",
    };

    // ESTIMATION PRICE
    const estimationPriceData = new Array(
      historicsPriceDataset?.data?.length - 1
    ).fill(null);
    const lastHistoricPricePoint = historicsPriceDataset.data.slice(-1).pop();

    historicsPriceDataset.data.push(
      ...new Array(12 - historicsPriceDataset?.data?.length).fill(null)
    );
    estimationPriceData.push({ ...lastHistoricPricePoint, ignore: true });

    const auxDate = moment(lastHistoricPricePoint.date);

    while (auxDate.add(1, "month") <= maxDate) {
      estimationPriceData.push({
        date: auxDate.format("YYYY-MM-DD"),
        x: auxDate.format("MMM'YY"),
        y: targetData?.selectedPrice,
      });
    }

    const estimationsPriceDataset = {
      ...estimationsDataset,
      yAxisID: "price",
      data: estimationPriceData,
    };

    // ESTIMATION UNITS
    const lastHistoricUnitsPoint = historicsUnitsDataset.data.slice(-1).pop();
    const estimationUnitsData = new Array(
      historicsUnitsDataset?.data?.length - 1
    ).fill(null);

    historicsUnitsDataset.data.push(
      ...new Array(12 - historicsUnitsDataset?.data?.length).fill(null)
    );
    estimationUnitsData.push({ ...lastHistoricUnitsPoint, ignore: true });

    const correctedEstimations = correctAndReduceEstimations(
      targetData,
      sourceData
    );

    estimationUnitsData.push(
      ..._.map(correctedEstimations, (estimation, key) => ({
        date: key,
        x: moment(key).format("MMM'YY"),
        y: estimation.sum_units,
      }))
    );

    const estimationsUnitsDataset = {
      ...estimationsDataset,
      yAxisID: "units",
      data: _.orderBy(estimationUnitsData, (item) =>
        moment(item?.date, "YYYY-MM-DD")
      ),
    };

    return [
      historicsPriceDataset,
      historicsUnitsDataset,
      estimationsPriceDataset,
      estimationsUnitsDataset,
    ];
  };

  const [
    ownHistoricsPriceDataset,
    ownHistoricsUnitsDataset,
    ownEstimationsPriceDataset,
    ownEstimationsUnitsDataset,
  ] = getDatasets(data?.own, data?.competitors, "#10A2B1");

  const [
    competitorsHistoricsPriceDataset,
    competitorsHistoricsUnitsDataset,
    competitorsEstimationsPriceDataset,
    competitorsEstimationsUnitsDataset,
  ] = getDatasets(data?.competitors, data?.own, "#FFC84A");

  const timeSeriesChartData = [
    {
      title: priceOutLabelByTenant,
      data: {
        labels,
        title: priceOutLabelByTenant,
        axisName: "price",
        datasets: [
          ownHistoricsPriceDataset,
          ownEstimationsPriceDataset,
          competitorsHistoricsPriceDataset,
          competitorsEstimationsPriceDataset,
        ],
      },
    },
    {
      title: sellOutLabelByTenant,
      data: {
        labels,
        title: sellOutLabelByTenant,
        axisName: "units",
        datasets: [
          ownHistoricsUnitsDataset,
          ownEstimationsUnitsDataset,
          competitorsHistoricsUnitsDataset,
          competitorsEstimationsUnitsDataset,
        ],
      },
    },
  ];
  return (
    <>
      {_.map(timeSeriesChartData, (chartData, i) => {
        return (
          <Box className={classes.sectionContainer} key={i}>
            <Box className={`${classes.flexCenter} ${classes.alignBaseline}`}>
              <Typography variant="body1" className={classes.sectionTitle}>
                {chartData.title}
              </Typography>
              <Typography variant="caption" style={{ marginLeft: "5px" }}>
                (History & Predictions)
              </Typography>
            </Box>
            <Box className={classes.chartcontainer}>
              <TimeSeriesChartComponent data={chartData.data} />
            </Box>
          </Box>
        );
      })}
    </>
  );
}

export default PriceTimeSeriesComponent;
